// https://chatgpt.com/c/691b7e40-15bc-832e-8a3b-4edbc5c03a0f
"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { Search, Menu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import CartButton from "./cart-button";
import NextDeliveryInfo from "./next-delivery-info";

export default function CatalogHeader({ searchStr }: { searchStr: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const [inputValue, setInputValue] = useState(searchStr);
  const lastPushedRef = useRef(searchStr);
  const { toggleSidebar } = useSidebar();

  // Sync input with URL param only when it changes externally
  // (e.g. back/forward navigation), not from our own debounced update
  useEffect(() => {
    if (searchStr !== lastPushedRef.current) {
      setInputValue(searchStr);
    }
    lastPushedRef.current = searchStr;
  }, [searchStr]);

  // Debounce URL updates
  useEffect(() => {
    const timeout = setTimeout(() => {
      const next = inputValue || "";
      if (next !== lastPushedRef.current) {
        lastPushedRef.current = next;
        if (inputValue) {
          router.replace(`${pathname}?q=${encodeURIComponent(inputValue)}`);
        } else {
          router.replace(pathname);
        }
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [inputValue, pathname, router]);

  return (
    <header className="z-50 border-b bg-background/80 backdrop-blur">
      <div className="flex h-16 md:h-16 items-center px-4 md:pr-12 md:pl-2">
        {/* LEFT: Sidebar toggle + Logo */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="hidden md:inline-flex h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground cursor-pointer mr-2"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <Link href="/" className="mr-4 flex items-center md:-mt-[8px] gap-2">
          <div className="relative h-14 md:h-16 w-auto aspect-square">
            <Image
              src="/calii_logo.png"
              alt="Logo"
              fill
              className="object-contain"
              priority
            />
          </div>
        </Link>

        {/* DELIVERY INFO */}
        <div className="hidden sm:flex mr-2 shrink-0 min-w-[180px] max-w-[300px] w-[20vw]">
          <NextDeliveryInfo />
        </div>

        {/* CENTER: max-width search bar area */}
        <div className="flex flex-1 justify-center">
          <div className="w-full max-w-2xl px-1 md:px-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar..."
                className="pl-10"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* RIGHT: Cart */}
        <div className="flex items-center gap-2">
          <CartButton />
        </div>
      </div>
    </header>
  );
}
